import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { Sales, SalesByLocation } from 'queries/brands/brand-sales'
import { formatCurrency } from 'utils/currency-helper'
import {
    Cell,
    formatCellValueForDollarDisplay,
    pickColumns,
    TableData,
} from 'utils/data'
import { isStore } from 'utils/location'

// trims brandname for use in URLs
export const getTrimmedBrandName = (brandName: string): string => {
    return brandName
        .replace(/[^a-zA-Z0-9]/g, '')
        .replace(/\s+/g, '')
        .toLowerCase()
}

export const safePickColumns = (
    tableData: TableData | undefined,
    columns: number[],
    t: (key: string) => string,
) => {
    const { brands } = TRANSLATION_KEYS
    if (!tableData || !Array.isArray(tableData.rows)) {
        return { rows: [], headings: [], footer: [] }
    }
    const headings: Cell[] = [
        { value: t(brands.headings.location) },
        { value: t(brands.headings.sales) },
        { value: t(brands.headings.orgSales) },
        { value: t(brands.headings.fulSales) },
    ]
    const pickedTable = pickColumns(tableData, columns)

    // Use shared formatter for columns after index 0
    const formattedRows = pickedTable.rows.map((row) =>
        row.map((cell, index) =>
            index === 0 ? cell : formatCellValueForDollarDisplay(cell),
        ),
    )

    return {
        ...pickedTable,
        rows: formattedRows,
        headings,
    }
}
// Converts intra day sales location data into a table format
export const buildSalesByLocationTableData = (
    salesByLocation: SalesByLocation,
    t: (key: string) => string,
): TableData => {
    const { brands } = TRANSLATION_KEYS
    if (!salesByLocation?.day?.length) {
        return {
            headings: [],
            rows: [],
            footer: [],
        }
    }

    const dayData = salesByLocation.day[0]
    const headings: Cell[] = [
        { value: t(brands.headings.location) },
        { value: t(brands.headings.sales) },
        { value: t(brands.headings.orgSales) },
        { value: t(brands.headings.fulSales) },
    ]
    const rows: Cell[][] = []

    if (dayData.parent_locations?.length) {
        dayData.parent_locations.forEach((parent) => {
            rows.push([
                { value: String(parent.location_description) },
                { value: formatCurrency(parent.sales) },
                { value: formatCurrency(parent.originated) },
                { value: formatCurrency(parent.fulfilled) },
            ])
        })
    }

    if (dayData.current_location) {
        rows.push([
            { value: String(dayData.current_location.location_description) },
            { value: formatCurrency(dayData.current_location.sales) },
            { value: formatCurrency(dayData.current_location.originated) },
            { value: formatCurrency(dayData.current_location.fulfilled) },
        ])
    }

    if (dayData.children_locations?.length) {
        dayData.children_locations.forEach((child) => {
            rows.push([
                { value: String(child.location_description) },
                { value: formatCurrency(child.sales) },
                { value: formatCurrency(child.originated) },
                { value: formatCurrency(child.fulfilled) },
            ])
        })
    }

    return {
        headings,
        rows,
    }
}

// Transforms historical sales data from table to match intra day format
export const transformTableData = (
    table: TableData,
    t: (key: string) => string,
): { formattedData: SalesData[]; totalSales: string } => {
    const { brands } = TRANSLATION_KEYS

    const originatedRow =
        table.rows.find((row) => row[0]?.value === 'ORIGINATED') || []
    const shipFromStoreRow =
        table.rows.find((row) => row[0]?.value === 'SHIP TO HOME') || []
    const orderPickupRow =
        table.rows.find((row) => row[0]?.value === 'STORE PICK-UP') || []
    const compRow = table.footer || []
    const formattedData = [
        {
            key: t(brands.headings.originated),
            value: originatedRow[1]?.value || '',
            percentage: originatedRow[2]?.value || '',
        },
        {
            key: t(brands.headings.shipFromStore),
            value: shipFromStoreRow[1]?.value || '',
            percentage: shipFromStoreRow[2]?.value || '',
        },
        {
            key: t(brands.headings.orderPickup),
            value: orderPickupRow[1]?.value || '',
            percentage: orderPickupRow[2]?.value || '',
        },
        {
            key: t(brands.headings.compPercent),
            value: null, // No value for comp %
            percentage: compRow[3]?.value || '',
        },
    ]

    const totalSales = table.footer?.[1]?.value || ''

    return {
        formattedData,
        totalSales,
    }
}

export type SalesData = {
    key: string
    value: number | string | null
    percentage: string | number
}
export type DayDataAsList = {
    formattedData: SalesData[]
    totalSales: number | null
}
// formats the day data into a list of objects with keys, values, and percentages
export const formatDayDataAsList = (
    day: Sales | null | undefined,
    t: (key: string) => string,
): DayDataAsList => {
    if (!day) {
        return { formattedData: [], totalSales: null }
    }
    const { brands } = TRANSLATION_KEYS

    const totalSales = day.sales
    const formattedData: SalesData[] = []
    if (day.sales_breakdown?.originated !== undefined) {
        formattedData.push({
            key: t(brands.headings.originated),
            value: day.sales_breakdown.originated,
            percentage: `${day.sales_breakdown_percentage?.originated ?? 0}%`,
        })
    }

    if (day.sales_breakdown?.ship_from_store !== undefined) {
        formattedData.push({
            key: t(brands.headings.shipFromStore),
            value: day.sales_breakdown.ship_from_store,
            percentage: `${day.sales_breakdown_percentage?.ship_from_store ?? 0}%`,
        })
    }

    if (day.sales_breakdown?.order_pickup !== undefined) {
        formattedData.push({
            key: t(brands.headings.orderPickup),
            value: day.sales_breakdown.order_pickup,
            percentage: `${day.sales_breakdown_percentage?.order_pickup ?? 0}%`,
        })
    }

    if (day.sales_percentage !== undefined) {
        formattedData.push({
            key: t(brands.headings.compPercent),
            value: null,
            percentage: day.sales_percentage,
        })
    }

    return { formattedData, totalSales }
}

// Function to transform Ulta loyalty data for table display
export const transformUltaLoyaltyData = (
    data: TableData | null | undefined,
    currentLocation: string,
    pathname: string,
    t: (key: string) => string,
): TableData => {
    const { brands } = TRANSLATION_KEYS
    if (!data?.headings || !data?.rows) {
        return { headings: [], rows: [], footer: [] }
    }

    const transformedHeadings = [
        { value: t(brands.headings.location) },
        { value: t(brands.headings.loyaltyOrigTrans) },
    ]

    const transformedTable: TableData = {
        headings: transformedHeadings,
        rows: data.rows || [],
        footer: data.footer || [],
    }

    transformedTable.rows = transformedTable.rows.map((row) =>
        row.map((cell) => {
            if (cell && typeof cell === 'object' && 'color' in cell) {
                const { color, ...cellWithoutColor } = cell
                return cellWithoutColor
            }
            return cell
        }),
    )

    const isStoreLocation = isStore(currentLocation)
    if (isStoreLocation) {
        transformedTable.rows = transformedTable.rows.map((row) => {
            if (row && row.length > 0) {
                return [{ value: currentLocation }, ...row.slice(1)]
            }
            return row
        })
    } else {
        // For non-store locations, add hyperlinks to first column
        transformedTable.rows = transformedTable.rows.map((row) => {
            if (row && row.length > 0) {
                const firstCell = row[0]
                if (
                    firstCell &&
                    typeof firstCell === 'object' &&
                    firstCell.value !== currentLocation
                ) {
                    const currentUrl = new URL(window.location.href)
                    const url = new URL(pathname, window.location.origin)
                    url.search = currentUrl.search
                    url.searchParams.set('locationId', firstCell.value || '')
                    row[0] = {
                        ...firstCell,
                        url: url.pathname + url.search,
                    }
                }
            }
            return row
        })
    }

    return transformedTable
}
